import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import ProgressIndicator from 'components/ProgressIndicator';
import Back from 'components/Back';
import SummaryReportPageHeader from 'components/SummaryReportPageHeader';
import {
  getIterationById,
  getIterationDetailsById,
} from 'utils/iterationUtils';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign';
import useFetchSummaryReport from 'queries/fetch/fetchSummaryReport';
import { SummaryReport } from 'schemas/SummaryReport.schema';
import SetSelectedSummaryReportReviewers from 'components/SummaryReport/SetSelectedSummaryReportReviewers.tsx';
import { CognitoUser } from 'schemas/UserInfo.schema.ts';
import useUpdateIterationStatus from 'queries/mutate/updateIterationStatus.ts';
import { ITERATION_STATUS } from 'schemas/CampaignApiDetails.schema.ts';
import usePatchReportSelectedReviewers from 'queries/mutate/patchReportSelectedReviewers.ts';
import ApiErrorDetails from 'components/ApiErrorDetails';
import { ApiError } from 'schemas/ApiError.schema';
import { API_UPDATE_ITERATION_ERROR } from 'constants/errorMessages';

function SummaryReportRequestReviewersPage() {
  const [error, setError] = useState<ApiError | null>(null);

  const {
    teamId, campaignId, iterationId, batchId,
  } = useParams();
  const fetchSpecificCampaign = useFetchSpecificCampaign({
    teamId: teamId!,
    campaignId: campaignId!,
  });
  const fetchSummaryReports = useFetchSummaryReport({
    teamId: teamId!,
    campaignId: campaignId!,
    iterationId: iterationId!,
  });

  const updateIterationStatus = useUpdateIterationStatus();
  const patchReportSelectedReviewers = usePatchReportSelectedReviewers();
  const navigate = useNavigate();

  if (
    !fetchSummaryReports.data
    || !fetchSpecificCampaign.data
    || fetchSummaryReports.status !== 'success'
    || fetchSpecificCampaign.status !== 'success'
  ) {
    return <ProgressIndicator text="Loading summary report..." />;
  }

  const currentIteration = getIterationById(
    fetchSpecificCampaign.data!,
    iterationId!,
  );
  const currentIterationDetails = getIterationDetailsById(
    fetchSpecificCampaign.data!,
    iterationId!,
  );
  const report: SummaryReport | undefined = fetchSummaryReports.data?.find(
    (r) => r.batch_id === batchId,
  );
  const reportIndex = fetchSummaryReports.data?.findIndex(
    (r) => r.batch_id === batchId,
  );
  const isLatestSummaryReport = reportIndex === fetchSummaryReports.data.length - 1;

  if (!report) {
    throw new Error(API_UPDATE_ITERATION_ERROR);
  }

  if (!currentIterationDetails) {
    return <ProgressIndicator text="Loading iteration details..." />;
  }

  return (
    <div className="iteration-reviewers-page">
      <div className="nhsuk-u-margin-bottom-6">
        <Back label="Back to report" backTo={`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary/reports/${batchId}`} />
      </div>
      <SummaryReportPageHeader
        title="Report Reviewers"
        iteration={currentIteration!}
        iterationDetails={currentIterationDetails!}
        report={report}
        summaryUrl={`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary`}
        isLatestSummaryReport={isLatestSummaryReport}
      />
      <SetSelectedSummaryReportReviewers
        teamId={teamId!}
        iterationCreator={currentIterationDetails.created_by}
        onClickSubmit={async (reviewers: CognitoUser[]) => {
          try {
            await patchReportSelectedReviewers.mutateAsync({
              teamId: teamId!,
              campaignId: campaignId!,
              iterationId: iterationId!,
              batchId: batchId!,
              summaryReportPatch: {
                REVIEWERS: reviewers.map((r) => r.sub),
              },
            });

            await updateIterationStatus.mutateAsync({
              status: ITERATION_STATUS.REPORT_IN_REVIEW,
              iterationId: iterationId!,
              campaignId: campaignId!,
              teamId: teamId!,
            });

            navigate(`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary/reports`);
          } catch {
            setError({ message: API_UPDATE_ITERATION_ERROR });
          }
        }}
      />
      <ApiErrorDetails apiError={error} />
    </div>
  );
}

export default SummaryReportRequestReviewersPage;
